import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, MessageSquare, Mail, Phone, Check, ExternalLink, UserCheck } from 'lucide-react';

const STATUS_STYLES = {
  new: { label: 'New', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  cm_introduced: { label: 'Introduced', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed: { label: 'Closed', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const EcosystemInquiriesTab = ({ api }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewing, setViewing] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/ecosystem/cm/inquiries', { params: { status: statusFilter } });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, status) => {
    try {
      await api.patch(`/ecosystem/cm/inquiries/${id}`, { status, cm_notes: notesDraft || null });
      toast.success(`Marked as ${STATUS_STYLES[status]?.label || status}`);
      setViewing(null);
      setNotesDraft('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const open = (it) => {
    setViewing(it);
    setNotesDraft(it.cm_notes || '');
  };

  return (
    <div className="space-y-4" data-testid="ecosystem-inquiries-tab">
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="cm_introduced">Introduced</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load}>Refresh</Button>
        <span className="text-sm text-muted-foreground">{items.length} inquiries</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No inquiries yet.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {items.map((it) => {
            const st = STATUS_STYLES[it.status] || STATUS_STYLES.new;
            return (
              <Card key={it.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-3 items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <h3 className="font-bold text-sm">{it.requester_name}{it.requester_company ? ` · ${it.requester_company}` : ''}</h3>
                        <Badge className={`${st.cls} border text-[10px]`}>{st.label}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1.5">
                        wants to connect with <strong className="text-foreground">{it.company_name}</strong> · {new Date(it.created_at).toLocaleString()}
                      </p>
                      <p className="text-sm text-[#5c5750] line-clamp-2">{it.requirement}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => open(it)} data-testid={`inq-open-${it.id}`}>Open</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-lg">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Inquiry &middot; {viewing.company_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2 text-sm">
                <div className="grid grid-cols-2 gap-2 bg-[#faf6ec] rounded-lg border p-3">
                  <div><p className="text-xs text-muted-foreground">From</p><p className="font-medium">{viewing.requester_name}</p></div>
                  <div><p className="text-xs text-muted-foreground">Company</p><p className="font-medium">{viewing.requester_company || '—'}</p></div>
                  <div className="col-span-2 flex flex-wrap gap-3">
                    <a href={`mailto:${viewing.requester_email}`} className="text-[#c8a97e] inline-flex items-center gap-1 text-xs"><Mail className="w-3.5 h-3.5" /> {viewing.requester_email}</a>
                    <a href={`tel:${viewing.requester_phone}`} className="text-[#c8a97e] inline-flex items-center gap-1 text-xs"><Phone className="w-3.5 h-3.5" /> {viewing.requester_phone}</a>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Requirement</p>
                  <p className="text-sm bg-white border rounded-md p-3 whitespace-pre-wrap">{viewing.requirement}</p>
                </div>
                <a href={`/ecosystem/c/${viewing.company_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#c8a97e]">
                  <ExternalLink className="w-3.5 h-3.5" /> View target company profile
                </a>
                <Textarea placeholder="Your internal notes — e.g. introduced via WhatsApp on 23rd..." value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} rows={3} />
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {viewing.status !== 'closed' && (
                  <Button variant="outline" onClick={() => update(viewing.id, 'closed')}>Close inquiry</Button>
                )}
                {viewing.status !== 'cm_introduced' && (
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => update(viewing.id, 'cm_introduced')} data-testid="inq-mark-introduced">
                    <UserCheck className="w-4 h-4 mr-1.5" /> Mark as Introduced
                  </Button>
                )}
                {viewing.status === 'cm_introduced' && (
                  <Button variant="outline" onClick={() => update(viewing.id, 'cm_introduced')}>
                    <Check className="w-4 h-4 mr-1.5" /> Save note
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
